import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { calculateRealTA } from '../utils/technicalAnalysis';
import { fetchOHLCV, fetchTokenData } from '../utils/tokenApi';
import { cn } from '../utils/cn';

const { FiActivity, FiTrendingUp, FiTrendingDown, FiTarget, FiBarChart2, FiCrosshair, FiCpu, FiLoader, FiShield, FiArrowDownRight, FiLayers, FiAlignRight } = FiIcons;

const formatTokenPrice = (price) => {
  if (!price) return '0.00';
  if (price < 0.00001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

export default function PositionAnalysis({ position, onApplyATR, botTimeframe }) {
  const { entryPrice, currentPrice, tokenAddress, pairAddress } = position;
  const [timeframe, setTimeframe] = useState(botTimeframe || '15m'); // Defaults to the configured bot timeframe
  const [ta, setTa] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadRealtimeAnalysis = async () => {
      setIsLoading(true);
      try {
        let activePair = pairAddress;
        if (!activePair) {
          const freshData = await fetchTokenData(tokenAddress);
          activePair = freshData.pairAddress;
        }

        if (activePair) {
          const ohlcv = await fetchOHLCV(activePair, timeframe);
          if (mounted) {
            const analysis = calculateRealTA(ohlcv, currentPrice, entryPrice);
            setTa(analysis);
          }
        }
      } catch (err) {
        console.error("Failed to load TA", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadRealtimeAnalysis();
    return () => { mounted = false; };
  }, [tokenAddress, pairAddress, currentPrice, entryPrice, timeframe]);

  if (isLoading || !ta) {
    return (
      <div className="border-t border-slate-700/50 mt-4 pt-8 pb-4 flex flex-col items-center justify-center">
        <SafeIcon icon={FiLoader} className="animate-spin text-purple-500 text-2xl mb-2" />
        <p className="text-xs text-slate-500 font-medium">Fetching real OHLCV arrays...</p>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-700/50 mt-4 pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCpu} className="text-purple-400" />
          <h4 className="text-xs font-bold text-white uppercase tracking-wider">AI Strategy Analysis</h4>
        </div>
        <div className="flex bg-[#0B0E14] rounded-lg p-1 border border-slate-800">
          {['1m', '5m', '15m', '1h', '4h'].map(tf => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={cn(
                "px-2.5 py-1 text-[10px] font-bold rounded-md transition-colors",
                timeframe === tf ? "bg-purple-500/20 text-purple-400 border border-purple-500/20 shadow-sm" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 border border-transparent"
              )}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      <div className={cn("p-3 rounded-lg border flex items-start gap-3 mb-4", ta.bgColor)}>
        <div className={cn("text-xl mt-0.5", ta.signalColor)}>
          <SafeIcon icon={ta.signal.includes('BUY') ? FiTrendingUp : (ta.signal.includes('SELL') ? FiTrendingDown : FiActivity)} />
        </div>
        <div>
          <div className={cn("text-sm font-black tracking-wide", ta.signalColor)}>
            SIGNAL: {ta.signal}
          </div>
          <p className="text-[11px] text-slate-300 mt-1 leading-relaxed">
            {ta.reasoning}
          </p>
        </div>
      </div>

      {/* ATR Dynamic Volatility Risk Suggestions */}
      <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
            <SafeIcon icon={FiShield} className="text-blue-400" />
            Suggested Risk Settings (ATR-Based)
          </h5>
          {!position.botTriggered && onApplyATR && (
            <button
              onClick={() => onApplyATR(ta.suggestions)}
              className="text-[9px] font-bold uppercase tracking-wider bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 px-2 py-1 rounded transition-colors"
              title="Apply to Risk Parameters"
            >
              Apply to Trade
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="border border-slate-800/80 rounded bg-slate-900/30 p-2 text-center">
            <div className="text-[9px] text-slate-500 uppercase font-bold mb-1 flex items-center justify-center gap-1">
              <SafeIcon icon={FiTrendingDown} className="text-red-400" /> Hard Stop
            </div>
            <div className="text-red-400 font-mono text-sm font-bold">
              {ta.suggestions.hardStop.pct.toFixed(2)}%
            </div>
            <div className="text-[9px] text-slate-400 font-mono mt-0.5">
              ${formatTokenPrice(ta.suggestions.hardStop.price)}
            </div>
          </div>
          <div className="border border-slate-800/80 rounded bg-slate-900/30 p-2 text-center">
            <div className="text-[9px] text-slate-500 uppercase font-bold mb-1 flex items-center justify-center gap-1">
              <SafeIcon icon={FiTarget} className="text-green-400" /> Take Profit
            </div>
            <div className="text-green-400 font-mono text-sm font-bold">
              {ta.suggestions.takeProfit.pct.toFixed(2)}%
            </div>
            <div className="text-[9px] text-slate-400 font-mono mt-0.5">
              ${formatTokenPrice(ta.suggestions.takeProfit.price)}
            </div>
          </div>
          <div className="border border-slate-800/80 rounded bg-slate-900/30 p-2 text-center">
            <div className="text-[9px] text-slate-500 uppercase font-bold mb-1 flex items-center justify-center gap-1">
              <SafeIcon icon={FiArrowDownRight} className="text-orange-400" /> Trail Stop
            </div>
            <div className="text-orange-400 font-mono text-sm font-bold">
              {ta.suggestions.trailingStop.pct.toFixed(2)}%
            </div>
            <div className="text-[9px] text-slate-400 font-mono mt-0.5">
              Dynamic
            </div>
          </div>
        </div>
      </div>

      {/* Technical Indicators */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        <IndicatorCard icon={FiActivity} title="RSI (14)" value={ta.indicators.rsi.value} status={ta.indicators.rsi.status} isBullish={ta.indicators.rsi.status === 'Oversold'} isBearish={ta.indicators.rsi.status === 'Overbought'} />
        <IndicatorCard icon={FiTrendingUp} title="MACD" value={ta.indicators.macd.value} status={ta.indicators.macd.status} isBullish={ta.indicators.macd.status.includes('Bullish')} isBearish={ta.indicators.macd.status.includes('Bearish')} />
        <IndicatorCard icon={FiTarget} title="EMA (20)" value={`$${ta.indicators.ema.value}`} status={ta.indicators.ema.status} isBullish={ta.indicators.ema.status.includes('Above')} isBearish={ta.indicators.ema.status.includes('Below')} />
        <IndicatorCard icon={FiTarget} title="SMA (20)" value={`$${ta.indicators.sma.value}`} status={ta.indicators.sma.status} isBullish={ta.indicators.sma.status.includes('Above')} isBearish={ta.indicators.sma.status.includes('Below')} />
        <IndicatorCard icon={FiCrosshair} title="Bollinger" value={ta.indicators.bb.value} status={ta.indicators.bb.status} isBullish={ta.indicators.bb.status.includes('Lower')} isBearish={ta.indicators.bb.status.includes('Upper')} />
        <IndicatorCard icon={FiActivity} title="ATR (14)" value={ta.indicators.atr.value} status={ta.indicators.atr.status} />
        <IndicatorCard icon={FiActivity} title="Stochastic" value={ta.indicators.stoch.value} status={ta.indicators.stoch.status} isBullish={ta.indicators.stoch.status === 'Oversold'} isBearish={ta.indicators.stoch.status === 'Overbought'} />
        <IndicatorCard icon={FiTrendingUp} title="ADX / DI" value={ta.indicators.adx.value} status={ta.indicators.adx.status} isBullish={ta.indicators.adx.status.includes('Bullish')} isBearish={ta.indicators.adx.status.includes('Bearish')} />
        <IndicatorCard icon={FiBarChart2} title="OBV" value={ta.indicators.obv.value} status={ta.indicators.obv.status} isBullish={ta.indicators.obv.status === 'Accumulation'} isBearish={ta.indicators.obv.status === 'Distribution'} />
        <IndicatorCard icon={FiBarChart2} title="Rel Vol" value={ta.indicators.relVol.value} status={ta.indicators.relVol.status} isBullish={parseFloat(ta.indicators.relVol.value) > 1.5} isBearish={parseFloat(ta.indicators.relVol.value) < 0.5} />
        <IndicatorCard icon={FiTarget} title="VWAP" value={`$${ta.indicators.vwap.value}`} status={ta.indicators.vwap.status} isBullish={ta.indicators.vwap.status.includes('Above')} isBearish={ta.indicators.vwap.status.includes('Below')} />
      </div>

      {/* Advanced Key Levels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <SupportResistancePanel sr={ta.supportResistance} formatPrice={formatTokenPrice} />
        <FibonacciPanel fib={ta.fibonacci} formatPrice={formatTokenPrice} />
      </div>
    </div>
  );
}

function IndicatorCard({ icon, title, value, status, isBullish, isBearish }) {
  let statusColor = "text-slate-400";
  if (isBullish) statusColor = "text-green-400";
  if (isBearish) statusColor = "text-red-400";

  return (
    <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-2.5 flex flex-col justify-between">
      <div className="flex items-center gap-1.5 mb-1.5">
        <SafeIcon icon={icon} className="text-[10px] text-slate-500" />
        <span className="text-[10px] font-bold text-slate-500 uppercase">{title}</span>
      </div>
      <div>
        <div className="font-mono text-xs text-white mb-0.5">{value}</div>
        <div className={cn("text-[9px] font-bold uppercase", statusColor)}>
          {status}
        </div>
      </div>
    </div>
  );
}

function SupportResistancePanel({ sr, formatPrice }) {
  return (
    <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3">
      <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
        <SafeIcon icon={FiLayers} className="text-blue-400" /> Support & Resistance
      </h5>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[9px] text-red-400 font-bold uppercase mb-1">Resistance</div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">R3</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.r3)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">R2</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.r2)}</span>
          </div>
          <div className="flex justify-between items-center bg-red-500/10 border border-red-500/20 px-2 py-1.5 rounded">
            <span className="text-[10px] text-red-400 font-bold">R1</span>
            <span className="text-xs font-mono text-red-400">${formatPrice(sr.r1)}</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="text-[9px] text-green-400 font-bold uppercase mb-1">Support</div>
          <div className="flex justify-between items-center bg-green-500/10 border border-green-500/20 px-2 py-1.5 rounded">
            <span className="text-[10px] text-green-400 font-bold">S1</span>
            <span className="text-xs font-mono text-green-400">${formatPrice(sr.s1)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">S2</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.s2)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
            <span className="text-[10px] text-slate-500">S3</span>
            <span className="text-xs font-mono text-slate-300">${formatPrice(sr.s3)}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-between items-center bg-blue-500/10 border border-blue-500/20 px-3 py-2 rounded-lg">
        <span className="text-[10px] text-blue-400 font-bold uppercase">Pivot Point</span>
        <span className="text-sm font-mono text-blue-400 font-bold">${formatPrice(sr.pivot)}</span>
      </div>
    </div>
  );
}

function FibonacciPanel({ fib, formatPrice }) {
  return (
    <div className="bg-[#0B0E14] border border-slate-800 rounded-lg p-3">
      <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
        <SafeIcon icon={FiAlignRight} className="text-purple-400" /> Fibonacci Retracement
      </h5>
      <div className="space-y-1.5 mt-2">
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">23.6%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl236)}</span>
        </div>
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">38.2%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl382)}</span>
        </div>
        <div className="flex justify-between items-center bg-purple-500/10 border border-purple-500/20 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-purple-400">50.0%</span>
          <span className="text-sm font-mono text-purple-400 font-bold">${formatPrice(fib.lvl500)}</span>
        </div>
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">61.8%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl618)}</span>
        </div>
        <div className="flex justify-between items-center bg-slate-900/50 px-2 py-1.5 rounded">
          <span className="text-[10px] font-bold text-slate-400">78.6%</span>
          <span className="text-xs font-mono text-slate-300">${formatPrice(fib.lvl786)}</span>
        </div>
      </div>
    </div>
  );
}